import React, { type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/** Standard light slide shell: white canvas, generous safe area, column flow. */
export function Slide({
  children
}: {
  children: ReactNode;
}) {
  return <section className="relative flex h-full w-full flex-col overflow-hidden bg-[var(--bg)] p-[clamp(2rem,4vw,4rem)] font-sans" data-id="element-7" data-mp="1gpv6inrq8fee">
      {children}
    </section>;
}

/** Eyebrow + headline block used at the top of every content slide. */
export function SlideHeader({
  eyebrow,
  children
}: {
  eyebrow: string;
  children: ReactNode;
}) {
  return <Fade delay={0.1} className="mb-[clamp(1.25rem,2.2vw,2.25rem)] flex-shrink-0" data-id="element-8" data-mp="y05f8an6abr8">
      <p className="mb-[clamp(0.5rem,1vw,1rem)] text-[clamp(0.875rem,1.5vw,1.125rem)] font-semibold uppercase tracking-wider text-[var(--brand-green)]" data-id="element-9" data-mp="121gw9rrzdont">
        {eyebrow}
      </p>
      <h2 className="max-w-5xl text-[clamp(2rem,3.6vw,3.25rem)] font-bold leading-[1.08] text-[var(--fg)]" data-id="element-10" data-mp="o3sl5bnptvvy">
        {children}
      </h2>
    </Fade>;
}

/** Highlight span inside a headline. */
export function Hi({
  children
}: {
  children: ReactNode;
}) {
  return <span className="text-[var(--brand-green-light)]" data-id="element-11" data-mp="2fwvtipbfbhsy">{children}</span>;
}

/** Standard surface card. */
export function Card({
  children,
  className = ''
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`rounded-2xl border border-black/5 bg-[var(--surface)] p-[clamp(1.25rem,2.2vw,2.25rem)] shadow-sm ${className}`} data-id="element-12" data-mp="2oz50j57hgt1l">
      {children}
    </div>;
}

/** Dark brand-green card, for emphasis and figures. */
export function DarkCard({
  children,
  className = ''
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`rounded-2xl bg-[var(--brand-green)] p-[clamp(1.25rem,2.2vw,2.25rem)] text-white shadow-sm ${className}`} data-id="element-13" data-mp="267m0964uft39">
      {children}
    </div>;
}
export function CardLabel({
  children,
  tone = 'green'
}: {
  children: ReactNode;
  tone?: 'green' | 'light' | 'muted';
}) {
  const color = tone === 'light' ? 'text-[var(--brand-green-lighter)]' : tone === 'muted' ? 'text-[var(--muted)]' : 'text-[var(--brand-green)]';
  return <p className={`text-[clamp(0.7rem,0.95vw,0.9rem)] font-bold uppercase tracking-wider ${color}`} data-id="element-14" data-mp="3nbj31dkfgvt9">
      {children}
    </p>;
}

/** Entrance motion for secondary content. */
export function Fade({
  children,
  delay = 0,
  className = ''
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();
  if (reduceMotion) return <div className={className} data-id="element-15" data-mp="34l19j6rrtw6u">{children}</div>;
  return <motion.div className={className} initial={{
    opacity: 0,
    y: 18
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-16" data-mp="34k87uk7o5wvk">
      {children}
    </motion.div>;
}